import { CanvasTexture, LinearFilter, Sprite, SpriteMaterial } from 'three';
import { stopAll, type StopHandle } from '@/util';
import { type ObjectNode, objectNode, updateObject } from '@/lib/threeScene/nodes/object';
import type { SceneContext } from '@/lib/threeScene/SceneContext';
import type { NodeId } from '@/lib/threeScene/nodeId';
import { type RenderOrderNode, renderOrderNode, updateRenderOrder } from '@/lib/threeScene/nodes/renderOrder';
import { watchImmediate } from '@vueuse/core';
import type { Raw } from 'vue';

export type SpriteNode = ObjectNode<'sprite'> &
    RenderOrderNode & {
    material: Raw<SpriteMaterial> | null;
};

export function spriteNode(id: NodeId, properties?: Partial<SpriteNode>): SpriteNode {
    return {
        ...objectNode('sprite', id, properties),
        ...renderOrderNode(properties),
        material: properties?.material ?? null,
    };
}

export function updateSprite(context: SceneContext, node: SpriteNode): StopHandle {
    const sprite = new Sprite();

    return stopAll(
        updateObject(context, node, sprite),
        updateRenderOrder(node, sprite),
        watchImmediate(
            () => node.material,
            (material) => {
                if (material !== null) {
                    sprite.material = material;
                }
            },
        ),
    );
}

/**
 * Render a text label with a pill-shaped background onto a canvas-backed SpriteMaterial.
 * The sprite uses world-unit sizing so it zooms naturally with the orthographic camera.
 */
export function createLabelSpriteMaterial(
    text: string,
    color: string,
): { material: SpriteMaterial; aspectRatio: number } | null {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;

    const fontSize = 64;
    const paddingX = 32;
    const paddingY = 12;
    const borderRadius = 24;

    ctx.font = `bold ${fontSize}px 'Proxima Nova', sans-serif`;
    const textWidth = ctx.measureText(text).width;

    canvas.width = Math.ceil(textWidth + paddingX * 2);
    canvas.height = Math.ceil(fontSize + paddingY * 2);

    // Background pill
    ctx.fillStyle = 'rgba(240, 242, 245, 0.65)';
    roundRect(ctx, 0, 0, canvas.width, canvas.height, borderRadius);
    ctx.fill();

    // Text (font must be re-set after canvas resize)
    ctx.font = `bold ${fontSize}px 'Proxima Nova', sans-serif`;
    ctx.fillStyle = color;
    ctx.textBaseline = 'middle';
    ctx.fillText(text, paddingX, canvas.height / 2);

    const texture = new CanvasTexture(canvas);
    texture.minFilter = LinearFilter;
    texture.generateMipmaps = false;

    const material = new SpriteMaterial({ map: texture, depthTest: false });

    return { material, aspectRatio: canvas.width / canvas.height };
}

function roundRect(
    ctx: CanvasRenderingContext2D,
    x: number, y: number, w: number, h: number, r: number,
): void {
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.lineTo(x + w - r, y);
    ctx.quadraticCurveTo(x + w, y, x + w, y + r);
    ctx.lineTo(x + w, y + h - r);
    ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
    ctx.lineTo(x + r, y + h);
    ctx.quadraticCurveTo(x, y + h, x, y + h - r);
    ctx.lineTo(x, y + r);
    ctx.quadraticCurveTo(x, y, x + r, y);
    ctx.closePath();
}